import { Component, Input, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { TeamsService } from '../../../services/teams/teams.service';
import {
  Transaction,
  TransactionsService,
} from '../../../services/transactions/transactions.service';
import { IBankTransactionInterface } from '../analytics-peer-to-peer/peer-to-peer-transaction.interface';
import { IP2pParsedJsonInterface, IP2pTransactionInterface } from './transactions.interface';

@Component({
  selector: 'app-analytics-reimbursements',
  templateUrl: './analytics-reimbursements.component.html',
  styleUrls: ['./analytics-reimbursements.component.scss'],
})
export class AnalyticsReimbursementsComponent implements OnInit {
  @Input() analytics: any;
  @ViewChild(MatSort) sort!: MatSort;
  SCROLL_CHUNK = 1000;

  public columnsToDisplay = [
    'transactionDate',
    'user',
    'employeeNumber',
    'sender',
    'details',
    'amount',
    'category',
    'taxable',
    'status',
    'action',
  ];
  public statuses = [
    { number: 0, message: 'All', status: ['All'] },
    { number: 1, message: 'Paid', status: ['Paid', 'Reimbursement'] },
    { number: 2, message: 'Onetime', status: ['OnetimeRewardAllowance', 'OnetimeRewardWallit'] },
    { number: 3, message: 'P2P', status: ['fromP2P', 'P2P', 'movedP2p'] },
    { number: 4, message: 'TeamPrograms', status: ['TeamPrograms'] },
  ];
  public members: any;
  public p2p: any;
  public team: any;
  public items = new MatTableDataSource<Transaction>();
  private page = 0;
  private teamId = '';
  private searchValue = '';
  public userId = 'all';
  public form: UntypedFormGroup;
  public fileName = '';

  constructor(
    private transactionsService: TransactionsService,
    private teamsService: TeamsService,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.form = this.formBuilder.group({
      member: new UntypedFormControl(0),
      status: new UntypedFormControl(0),
    });
    this.form.valueChanges.subscribe(() => {
      const memberIndex = this.form.get('member')?.value;
      this.userId = memberIndex ? this.members[memberIndex].userId : 'all';
      this.loadData().then((results) => {
        this.items.data = results;
      });
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params.userId) {
        this.userId = params.userId;
      }
      this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
        if (wellspace) {
          this.teamId = wellspace.id;
          this.team = wellspace;
          this.teamsService.getTeamMembers(this.teamId).then((members) => {
            this.members = members.filter((thisMember: any) => thisMember.userId);
            this.members.unshift({ firstName: 'All', lastName: 'members' });
            const index = this.members.findIndex((thisMember: any) => thisMember.userId === this.userId);
            this.form.get('member')?.setValue(index < 0 ? 0 : index, { emitEvent: false });
            const member = this.members[index < 0 ? 0 : index];
            this.fileName = `${wellspace.name} reimbursements ${member.firstName} ${member.lastName}`;
            this.loadData().then((results) => {
              this.items.data = results;
              this.items.sort = this.sort;
            });
          });
        }
      });
    });
  }

  private async loadData(): Promise<any> {
    let results: any[] = [];
    // MM-3876: It was requested that the list include all transaction types,
    // with the exception of "Topup".
    const excludeStatus = 'Topup';
    await this.transactionsService
      .listReimbursements(
        this.teamId,
        this.page,
        this.SCROLL_CHUNK,
        this.searchValue,
        '',
        excludeStatus,
      )
      .then((res) => {
        results = res?.teams[0]?.items.filter(
          (obj: any, index: number, self: any) =>
            index === self.findIndex((t: any) => JSON.stringify(t) === JSON.stringify(obj)),
        );
      })
      .catch((err) => {
        console.error('Error in list Reimbursements', err);
        results = [];
      });
    if (this.userId !== 'all') {
      results = results.filter((item: any) => this.userId === item.userId);
    }
    if (this.searchValue) {
      const search = this.searchValue.toLowerCase();
      results = results.filter((item: any) => {
        const member = this.members.find((thisMember: any) => thisMember.userId === item.userId);
        return (
          item.employeeNumber?.toLowerCase().includes(search) ||
          item.status?.toLowerCase().includes(search) ||
          item.amount?.toString().includes(search) ||
          member.firstName?.toLowerCase().includes(search) ||
          member.lastName?.toLowerCase().includes(search) ||
          (!this.team.transactionPrivacy && item.details?.toLowerCase().includes(search))
        );
      });
    }
    // MM-3664: It was requested that the list include p2p transactions
    let p2pTransactions = await this.getAllPeerToPeerTransactions()
    p2pTransactions = p2pTransactions.filter((item: any) => item.status === 'P2P' || item.status === 'fromP2P')
    results = [...results, ...p2pTransactions];
    results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    results.forEach((result: any) => {
      result.employeeNumber = this.members.find(
        (member: any) => member.userId === result.userId,
      )?.employeeNumber;
    });
    const statusFilter = this.form.get('status')?.value;
    if (statusFilter !== 0) {
      results = results.filter((item: any) => this.statuses[statusFilter].status.includes(item.status));
    }
    return results;
  }

  async getAllPeerToPeerTransactions(): Promise<any> {
    console.log('Getting all peer transactions');
    try {
      const p2pTransactionsFromTransactionsTable =
        await this.transactionsService.getPeerToPeerTeamTransactionHistory(this.teamId);
      const formattedTransactions = this.formatP2pTransactions(
        p2pTransactionsFromTransactionsTable,
      );
      return formattedTransactions;
    } catch (error) {
      console.error(`Error in list Reimbursements in get all peer to peer transaction: ${error}`);
      return [];
    }
  }

  formatP2pTransactions(transactions: IBankTransactionInterface[]): IP2pTransactionInterface[] {
    const formattedTransactions: IP2pTransactionInterface[] = [];
    for (const transaction of transactions) {
      const parsedJSON: IP2pParsedJsonInterface = JSON.parse(transaction.sourceData);
      const formattedTransaction = {
        date: transaction.createdAt,
        userId: parsedJSON.destinationUserId,
        amount: transaction.amount,
        category: '---',
        info: 'P2P Transaction',
        sender: parsedJSON.fromUserId || parsedJSON.sourceFromUserId,
        status: transaction.transactionType,
        taxable: false,
      };
      formattedTransactions.push(formattedTransaction);
    }
    return formattedTransactions;
  }

  filterItems(event: any): void {
    this.searchValue = event.target.value;
    this.loadData().then((results) => {
      this.items.data = results;
    });
  }
}

@Pipe({
  name: 'getUserNameReimbursements',
  pure: true,
})
export class GetUserNameReimbursements implements PipeTransform {
  transform(item: any, thisArg: AnalyticsReimbursementsComponent): string {
    const member = thisArg.members.find((thisMember: any) => thisMember.userId === item.userId);
    return !member ? '---' : `${member.firstName ?? ''} ${member?.lastName ?? ''}`;
  }
}

@Pipe({
  name: 'getSenderNameReimbursements',
  pure: true,
})
export class GetSenderNameReimbursements implements PipeTransform {
  transform(item: any, thisArg: AnalyticsReimbursementsComponent): string {
    const member = thisArg.members.find((thisMember: any) => thisMember.userId === item.sender);
    return !member ? '---' : `${member.firstName ?? ''} ${member?.lastName ?? ''}`;
  }
}
